import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { VersionIndicator } from './VersionIndicator';
import type { WorkflowVersion } from '@shared/types/extended-features';

interface EditableWorkflowHeaderProps {
  name: string;
  description?: string;
  workflowId?: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onVersionRestore?: (version: WorkflowVersion) => void;
  versionRefreshTrigger?: number; // Add trigger to refresh version indicator
  currentVersionNumber?: number | null; // Track which version is currently loaded
  className?: string;
}

export function EditableWorkflowHeader({
  name,
  description,
  workflowId,
  onNameChange,
  onDescriptionChange,
  onVersionRestore,
  versionRefreshTrigger,
  currentVersionNumber,
  className,
}: EditableWorkflowHeaderProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [localName, setLocalName] = useState(name);
  const [localDescription, setLocalDescription] = useState(description || '');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  // Update local state when props change
  useEffect(() => {
    setLocalName(name);
  }, [name]);

  useEffect(() => {
    setLocalDescription(description || '');
  }, [description]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [isEditingDescription]);

  const handleNameBlur = () => {
    setIsEditingName(false);
    if (localName.trim() && localName !== name) {
      onNameChange(localName.trim());
    } else if (!localName.trim()) {
      setLocalName(name); // Revert if empty
    }
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
    if (localDescription !== description) {
      onDescriptionChange(localDescription);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setLocalName(name);
      setIsEditingName(false);
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleDescriptionBlur();
    } else if (e.key === 'Escape') {
      setLocalDescription(description || '');
      setIsEditingDescription(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      {/* Editable Name with Version Indicator */}
      <div className="flex items-center gap-2">
        {isEditingName ? (
          <Input
            ref={nameInputRef}
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            className="h-7 text-lg font-semibold px-2 py-0 border-primary flex-1"
            placeholder="Workflow name"
          />
        ) : (
          <h1
            className="text-lg font-semibold leading-tight cursor-text hover:text-primary transition-colors px-2 py-0.5 rounded hover:bg-accent/50 flex-1"
            onClick={() => setIsEditingName(true)}
            title="Click to edit workflow name"
          >
            {name}
          </h1>
        )}
        {workflowId && (
          <VersionIndicator 
            workflowId={workflowId} 
            onVersionRestore={onVersionRestore}
            refreshTrigger={versionRefreshTrigger}
            currentVersionNumber={currentVersionNumber}
          />
        )}
      </div>

      {/* Editable Description */}
      {isEditingDescription ? (
        <Textarea
          ref={descriptionInputRef}
          value={localDescription}
          onChange={(e) => setLocalDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          onKeyDown={handleDescriptionKeyDown}
          className="min-h-[20px] h-auto text-xs px-2 py-0.5 border-primary resize-none"
          placeholder="Add a description (Ctrl+Enter to save)"
          rows={1}
        />
      ) : (
        <p
          className="text-xs text-muted-foreground leading-tight cursor-text hover:text-foreground transition-colors px-2 py-0.5 rounded hover:bg-accent/50"
          onClick={() => setIsEditingDescription(true)}
          title="Click to edit workflow description"
        >
          {description || 'Add a description...'}
        </p>
      )}
    </div>
  );
}
